import React, { useState, useMemo, useEffect } from "react";
import FormControl from "@mui/material/FormControl";
import Autocomplete from "@mui/material/Autocomplete";
import TuneIcon from "@mui/icons-material/Tune";
import TextField from "@mui/material/TextField";
import { toSnakeCase } from "../utils/general_utils";

const textFieldStyles = { width: "100%" };

export default function InputFilter(props) {
  const {
    item,            // key in each row to read
    name,            // label
    data = [],
    icon,
    value: valueProp = null,
    callback,
    opt,
    defaultOpt = [],
  } = props;

  const [value, setValue] = useState(valueProp);
  const [inputValue, setInputValue] = useState("");
  const [isFocused, setIsFocused] = useState(false);

  const options = useMemo(() => {
    const detected = opt ?? data.map((row) => row?.[item]);
    const merged = [...defaultOpt, ...detected];

    // Remove null/undefined/empty, de-dup, stringify
    const unique = Array.from(new Set(merged))
      .filter((v) => v != null && v !== "")
      .map((v) => (typeof v === "string" ? v : String(v)))
      .sort((a, b) => a.localeCompare(b));

    return unique;
  }, [data, opt, defaultOpt, item]);

  // If current value is no longer present in options, clear it
  useEffect(() => {
    if (value != null && !options.some((o) => String(o) === String(value))) {
      setValue(null);
      callback?.(item, null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options]);

  return (
    <FormControl sx={textFieldStyles} size="small">
      <Autocomplete
        test_id={toSnakeCase("autocomplete_" + name)}
        size="small"
        options={options}
        value={value}
        onChange={(event, newValue) => {
          const next = newValue ?? null;
          setValue(next);
          callback?.(item, next);
        }}
        inputValue={inputValue}
        onInputChange={(event, newInputValue) => setInputValue(newInputValue)}
        // Critical: avoid reading `.label` on null/primitive
        getOptionLabel={(option) => (option == null ? "" : String(option))}
        // Make equality robust across string/number
        isOptionEqualToValue={(opt, val) => String(opt) === String(val)}
        renderInput={(params) => (
          <TextField
            {...params}
            test_id={toSnakeCase(name)}
            label={name}
            InputLabelProps={{
              shrink: isFocused || inputValue.length > 0,
              sx: {
                marginLeft: inputValue.length > 0 || isFocused ? 0 : "24px",
              },
            }}
            // Use InputProps for MUI v5; keeps compatibility
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <>
                  {icon ?? <TuneIcon />}
                  {params.InputProps?.startAdornment}
                </>
              ),
            }}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
          />
        )}
      />
    </FormControl>
  );
}
